import type { SkillCategory, SkillTag } from './types.js';

/**
 * 技能词典（单一数据源）。
 * 规则：
 * - id 稳定、只增不改；中文名用于展示。
 * - aliases 用于「一句话录入」时的归一化（大小写不敏感）。
 * - 未收录的词**不得**直接当成技能参与打分（解析层要标「待归类」）。
 */
export const SKILLS: SkillTag[] = [
  // 开发
  { id: 'python', name: 'Python', category: 'dev', aliases: ['python', 'py'] },
  { id: 'java', name: 'Java', category: 'dev', aliases: ['java'] },
  { id: 'javascript', name: 'JavaScript', category: 'dev', aliases: ['javascript', 'js'] },
  { id: 'typescript', name: 'TypeScript', category: 'dev', aliases: ['typescript', 'ts'] },
  { id: 'react', name: 'React', category: 'dev', aliases: ['react', 'react.js'] },
  { id: 'vue', name: 'Vue', category: 'dev', aliases: ['vue', 'vue3'] },
  { id: 'nodejs', name: 'Node.js', category: 'dev', aliases: ['node', 'nodejs', 'node.js'] },
  { id: 'sql', name: 'SQL/数据库', category: 'data', aliases: ['sql', 'mysql', '数据库'] },
  { id: 'frontend', name: '前端开发', category: 'dev', aliases: ['前端', 'frontend'] },
  { id: 'backend', name: '后端开发', category: 'dev', aliases: ['后端', 'backend', '服务端'] },
  { id: 'miniprogram', name: '小程序开发', category: 'dev', aliases: ['小程序', '微信小程序'] },
  { id: 'app', name: 'App 开发', category: 'dev', aliases: ['app', '安卓', 'ios', '移动端开发'] },
  { id: 'hardware', name: '硬件/嵌入式', category: 'dev', aliases: ['硬件', '嵌入式', '单片机', 'arduino', 'stm32'] },
  { id: 'algorithm', name: '算法/模型', category: 'dev', aliases: ['算法', '机器学习', '深度学习', 'ai模型'] },
  { id: 'git', name: 'Git/协作', category: 'dev', aliases: ['git', 'github', '版本管理'] },

  // 设计
  { id: 'figma', name: 'Figma', category: 'design', aliases: ['figma'] },
  { id: 'ps', name: 'Photoshop', category: 'design', aliases: ['ps', 'photoshop', '修图'] },
  { id: 'ui', name: 'UI 设计', category: 'design', aliases: ['ui', '界面设计', 'ui设计'] },
  { id: 'ux', name: 'UX/交互设计', category: 'design', aliases: ['ux', '交互', '交互设计', '用户体验'] },
  { id: 'poster', name: '海报/视觉', category: 'design', aliases: ['海报', '平面设计', '视觉设计', 'banner'] },
  { id: 'video', name: '视频剪辑', category: 'content', aliases: ['视频剪辑', '剪辑', 'pr', '剪映', 'ae'] },
  { id: '3d', name: '3D/建模', category: 'design', aliases: ['3d', '建模', 'blender', 'c4d'] },

  // 商业
  { id: 'bp', name: '商业计划书', category: 'business', aliases: ['商业计划书', 'bp', '企划书'] },
  { id: 'market', name: '市场调研', category: 'business', aliases: ['市场调研', '调研', '问卷设计'] },
  { id: 'finance', name: '财务测算', category: 'business', aliases: ['财务', '财务测算', '会计', '成本核算'] },
  { id: 'operation', name: '运营/增长', category: 'business', aliases: ['运营', '增长', '用户运营', '社群运营'] },
  { id: 'bd', name: '商务拓展', category: 'business', aliases: ['商务', 'bd', '合作洽谈', '拉赞助'] },
  { id: 'marketing', name: '市场营销', category: 'business', aliases: ['营销', '市场营销', '推广'] },
  { id: 'law', name: '法学/合规', category: 'domain', aliases: ['法学', '法律', '合规'] },
  { id: 'accounting', name: '会计/审计', category: 'domain', aliases: ['会计', '审计', '税务'] },
  { id: 'trade', name: '国际贸易', category: 'domain', aliases: ['国际贸易', '外贸', '进出口'] },
  { id: 'translation', name: '翻译', category: 'domain', aliases: ['翻译', '笔译', '口译'] },
  { id: 'english', name: '英语', category: 'domain', aliases: ['英语', '英文'] },
  { id: 'japanese', name: '日语', category: 'domain', aliases: ['日语'] },

  // 数据
  { id: 'excel', name: 'Excel', category: 'data', aliases: ['excel', '表格'] },
  { id: 'analysis', name: '数据分析', category: 'data', aliases: ['数据分析', '数据挖掘', 'spss', 'stata'] },
  { id: 'visualization', name: '数据可视化', category: 'data', aliases: ['数据可视化', '可视化', 'tableau', 'powerbi'] },
  { id: 'spss', name: '统计建模', category: 'data', aliases: ['统计', '建模', 'r语言', 'matlab'] },

  // 内容与表达
  { id: 'writing', name: '文案写作', category: 'content', aliases: ['文案', '写作', '撰稿'] },
  { id: 'ppt', name: 'PPT/路演材料', category: 'content', aliases: ['ppt', '路演材料', '汇报材料'] },
  { id: 'speech', name: '演讲/答辩', category: 'speech', aliases: ['演讲', '答辩', '路演', '主持'] },
  { id: 'photography', name: '摄影', category: 'content', aliases: ['摄影', '拍照'] },
  { id: 'newmedia', name: '新媒体运营', category: 'content', aliases: ['新媒体', '公众号', '小红书', '抖音'] },
  { id: 'copywriting', name: '脚本/策划', category: 'content', aliases: ['脚本', '策划', '创意'] },
  { id: 'projectmgmt', name: '项目管理', category: 'business', aliases: ['项目管理', '进度管理', '统筹'] },
];

export const SKILL_MAP: Record<string, SkillTag> = SKILLS.reduce(
  (acc, s) => {
    acc[s.id] = s;
    return acc;
  },
  {} as Record<string, SkillTag>,
);

export function skillName(id: string): string {
  return SKILL_MAP[id]?.name ?? id;
}

export function skillCategory(id: string): SkillCategory | null {
  return SKILL_MAP[id]?.category ?? null;
}

/** Levenshtein 距离（小词表用，O(n*m) 足够） */
export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min((cur[j - 1] ?? 0) + 1, (prev[j] ?? 0) + 1, (prev[j - 1] ?? 0) + cost);
    }
    prev = cur;
  }
  return prev[b.length] ?? Math.max(a.length, b.length);
}

/**
 * 把自由文本归一化成技能 id。
 * 返回 null 表示**未收录**（调用方必须标「待归类」，不得直接入分）。
 */
export function normalizeSkill(raw: string): string | null {
  const text = raw.trim().toLowerCase().replace(/\s+/g, '');
  if (!text) return null;
  // 1) 别名精确/包含匹配（长别名优先，避免 "ps" 命中 "spss"）；
  //    拉丁别名要求词边界（否则 "sports" 里的 "ts" 会误中 TypeScript），中文别名无边界概念
  const byAlias = SKILLS.flatMap((s) => (s.aliases ?? []).map((a) => ({ id: s.id, alias: a.toLowerCase() })))
    .filter((x) => x.alias.length >= 2)
    .sort((x, y) => y.alias.length - x.alias.length);
  const latinAliasRe = (alias: string): RegExp =>
    new RegExp(`(?<![a-z0-9])${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?![a-z0-9])`);
  for (const { id, alias } of byAlias) {
    if (text === alias) return id;
    if (/^[a-z0-9]/.test(alias)) {
      if (latinAliasRe(alias).test(text)) return id;
    } else if (text.includes(alias)) {
      return id;
    }
  }
  // 2) 中文名包含匹配
  for (const s of SKILLS) {
    if (text.includes(s.name.toLowerCase())) return s.id;
  }
  // 3) 编辑距离 ≤2 的模糊匹配（只对长度 ≥4 的词做，避免误伤）
  if (text.length >= 4) {
    let best: { id: string; dist: number } | null = null;
    for (const s of SKILLS) {
      for (const alias of [s.id, ...(s.aliases ?? [])]) {
        if (alias.length < 4) continue;
        const d = levenshtein(text, alias.toLowerCase());
        if (d <= 2 && (!best || d < best.dist)) best = { id: s.id, dist: d };
      }
    }
    if (best) return best.id;
  }
  return null;
}

/** 一次解析多个技能词，返回已收录 id 与未收录原词 */
export function normalizeSkills(raws: string[]): { matched: string[]; unmatched: string[] } {
  const matched = new Set<string>();
  const unmatched: string[] = [];
  for (const raw of raws) {
    const id = normalizeSkill(raw);
    if (id) matched.add(id);
    else unmatched.push(raw.trim());
  }
  return { matched: [...matched], unmatched };
}

/**
 * 技能近邻表（同域词）：AI 补位建议（ai-candidate.ts）用它把"缺口技能"扩成一张可信的画像。
 * 只收录 SKILLS 中的真实 id；手工标注，新增技能时按需补充。
 */
export const SKILL_NEIGHBORS: Record<string, readonly string[]> = {
  frontend: ['javascript', 'typescript', 'react', 'vue'],
  backend: ['nodejs', 'java', 'python', 'sql'],
  miniprogram: ['frontend', 'javascript', 'ui'],
  app: ['frontend', 'java', 'python'],
  algorithm: ['python', 'analysis', 'spss'],
  hardware: ['python', 'algorithm', 'excel'],
  ui: ['figma', 'ux', 'ps', 'poster'],
  ux: ['ui', 'market', 'writing'],
  poster: ['ps', 'figma', 'video'],
  video: ['poster', 'writing', 'ppt'],
  '3d': ['poster', 'ps', 'ui'],
  bp: ['finance', 'market', 'writing'],
  market: ['analysis', 'excel', 'operation'],
  finance: ['accounting', 'excel', 'bp'],
  operation: ['market', 'writing', 'bd'],
  bd: ['speech', 'market', 'english'],
  marketing: ['market', 'writing', 'poster'],
  analysis: ['excel', 'visualization', 'sql'],
  visualization: ['analysis', 'excel', 'ppt'],
  spss: ['analysis', 'algorithm', 'excel'],
  writing: ['ppt', 'translation', 'speech'],
  ppt: ['speech', 'writing', 'poster'],
  speech: ['ppt', 'english', 'writing'],
  translation: ['english', 'japanese', 'writing'],
};